import {Input, message, Radio, Select, Upload} from 'antd'
import {baseFieldTypes} from 'apis/base/baseFieldTypes'
import ImageLoader from 'components/ImageLoader'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {ActionModalWrapper} from 'components/form/ActionModalWrapper'
import {dataURItoBlob, getBase64} from 'components/form/fields/ImageUpload'
import {createValue} from 'components/form/utils'
import {Field, Formik, useFormikContext} from 'formik'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo} from 'react'
import {IoCloudUploadOutline} from 'react-icons/io5'
import {PiCertificate} from 'react-icons/pi'
import {AdvancedFormFields} from 'views/AppLocals/custom/AdvancedFormGroup'
import {createControlledFormField, createNumberControlledInput} from 'views/AppLocals/factory/createFormField'
import {deepTranslate, NullAsDefault, shallowDiff} from 'views/Shared'
import {medal_getMedals_Api} from '../../../apis'
import {bindQueryParams} from '../../AppLocals/functions/routerHelper'
import useAsync from '../../../modules/asyncCache/useAsync'
import {
    lengthBetween,
    noDoubleSpaces,
    noLeadingTrailingSpaces,
    noSpecialChars,
    required,
    runRules,
} from '../../../components/form/ValidateForm'

// ─── Certificate image upload zone ───────────────────────────────────────────
// Clickable full-area zone with hover reveal, handles preview + upload in one.

const MedalImageSection = ({name}) => {
    const t = useTranslate()
    const {setFieldValue} = useFormikContext()

    const beforeUpload = useCallback(
        (file) => {
            const isLt4M = file.size / 1024 / 1024 < 4
            if (!isLt4M) message.error(`${t('image must smaller than')} 4MB!`)
            const isImage = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
            if (!isImage) message.error(`${t('You can only upload JPG/PNG file')}!`)
            return false
        },
        [t]
    )

    const handleChange = useCallback(
        async (info) => {
            if (info?.file && !info.file.preview) {
                const file = await getBase64(info.file, 1200, 900)
                const image = dataURItoBlob(file)
                setFieldValue(name + '_preview', file)
                setFieldValue(name, image)
            }
        },
        [name, setFieldValue]
    )

    return (
        <Field name={name + '_preview'}>
            {({field}) => (
                <Upload
                    accept=".jpg,.jpeg,.png,.webp"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    onChange={handleChange}
                    style={{display: 'block', width: '100%'}}>
                    <div
                        style={{height: 192}}
                        className="group relative w-full rounded-xl overflow-hidden border-2 border-dashed border-color-50 background-100 flex items-center justify-center cursor-pointer hover:border-primary transition-colors duration-200">
                        {field.value ? (
                            <img
                                src={field.value}
                                alt=""
                                className="w-full h-full object-contain"
                            />
                        ) : (
                            <div className="flex flex-col items-center gap-3 select-none pointer-events-none">
                                <PiCertificate
                                    size={60}
                                    style={{color: '#d0d0d0'}}
                                />
                                <span className="text-xs text-color-400 italic">
                                    {t('click to upload')}
                                </span>
                            </div>
                        )}
                        {/* Hover badge */}
                        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                            <div className="flex items-center gap-1.5 bg-white rounded-lg px-3 py-1.5 shadow-sm border border-color-50">
                                <IoCloudUploadOutline
                                    size={13}
                                    className="text-color-300"
                                />
                                <span className="text-xs font-medium text-color-200">
                                    {t('upload image')}
                                </span>
                            </div>
                        </div>
                    </div>
                </Upload>
            )}
        </Field>
    )
}

// ─── Medal select with image thumbnail ───────────────────────────────────────
// Exported so other forms (CourseMedalForm, CurriculumForm) can reuse.

export const MedalSelectField = ({
    name,
    value,
    onChange,
    form,
    disabled = false,
    placeholder,
}) => {
    const t = useTranslate()
    const medals = _.get(form, 'values.medals', [])
    return (
        <Select
            disabled={disabled}
            className="w-full"
            placeholder={placeholder ?? t('click to select')}
            value={value || undefined}
            allowClear
            showSearch
            filterOption={(input, option) =>
                (option?.searchText ?? '').toLowerCase().includes(input.toLowerCase())
            }
            onChange={(v) => onChange(createValue(name, v ?? undefined))}
            options={medals.map((m) => ({
                value: m.id,
                searchText: m.title,
                label: (
                    <div className="flex items-center gap-2">
                        <ImageLoader
                            src={m.avatar}
                            style={{width: 28, height: 22, objectFit: 'contain'}}
                        />
                        <span className="text-sm">{m.title}</span>
                    </div>
                ),
            }))}
        />
    )
}

// ─── Tags select ──────────────────────────────────────────────────────────────
// Plain antd Select (mode=tags) — SelectWidget hides value when no options.

const TagsField = ({name, value, onChange}) => {
    const t = useTranslate()
    return (
        <Select
            mode="tags"
            className="w-full"
            placeholder={t('tags')}
            value={value}
            allowClear
            onChange={(v) => onChange(createValue(name, v))}
        />
    )
}

// ─── Form schema ──────────────────────────────────────────────────────────────

const formSchema = {
    avatar: {
        children: [
            {
                name: 'avatar',
                // MedalImageSection reads name + '_preview' from Formik directly.
                component: ({name}) => <MedalImageSection name={name}/>,
            },
        ],
    },
    title: {
        title: 'name of certification',
        children: {
            name: 'title',
            component: createControlledFormField({
                placeholder: 'name of certification',
                showCount: true,
                maxLength: 180,
            }),
        },
    },
    description: {
        title: 'description',
        children: {
            name: 'description',
            component: createControlledFormField({
                rows: 3,
                showCount: true,
                maxLength: 500,
                placeholder: 'description',
                Component: Input.TextArea,
            }),
        },
    },
    // Level (narrow) + Tags (wide) side by side
    levelAndTags: {
        inline: true,
        className: 'flex gap-3',
        itemClassNames: ['w-2/5', 'flex-1'],
        children: [
            {
                name: 'lvl_number',
                label: 'level',
                component: createNumberControlledInput({
                    placeholder: '1',
                    inputMode: 'number',
                    min: 1,
                    max: 1000,
                }),
            },
            {
                name: 'tags',
                label: 'tags',
                component: TagsField,
            },
        ],
    },
    active: {
        title: 'active',
        children: [
            {
                name: 'active',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <Radio.Group
                                name={name}
                                value={value}
                                onChange={onChange}
                                buttonStyle="solid"
                                optionType="button"
                                className="custom-radio-button"
                                options={baseFieldTypes.active.map(deepTranslate(t))}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },
    medal_1_id: {
        title: 'medal 1',
        children: [{name: 'medal_1_id', component: MedalSelectField}],
    },
    medal_2_id: {
        title: 'medal 2',
        children: [{name: 'medal_2_id', component: MedalSelectField}],
    },
    new_medal_id: {
        title: 'new medal',
        children: [{name: 'new_medal_id', component: MedalSelectField}],
    },
}

// ─── Validation ───────────────────────────────────────────────────────────────

const validate = (t) => (values) => {
    const {title, lvl_number, tags} = values ?? {}
    const errors = {}

    const titleMsg = runRules(title, [
        required(t),
        lengthBetween(2, 180, t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean))
    if (titleMsg) errors.title = titleMsg

    const lvlMsg = runRules(lvl_number, [required(t)].filter(Boolean))
    if (lvlMsg) errors.lvl_number = lvlMsg

    if (Array.isArray(tags) && tags.length > 0) {
        const tagRules = [
            lengthBetween(2, 255, t),
            noSpecialChars(t),
            noLeadingTrailingSpaces(t),
            noDoubleSpaces(t),
        ]
        for (const tag of tags) {
            const msg = runRules(tag, tagRules)
            if (msg) {
                errors.tags = msg
                break
            }
        }
    }

    return errors
}

// ─── Shared form body (Create + Edit reuse) ───────────────────────────────────

const MedalFormBody = ({isSubmitting, submitLabel}) => (
    <ActionModalWrapper
        className="gap-4"
        action={
            <FormActionGroups
                isSubmitting={isSubmitting}
                submitProps={{label: submitLabel}}
            />
        }>
        <FieldsFactory
            formSchema={[
                formSchema.avatar,
                formSchema.title,
                formSchema.description,
                formSchema.levelAndTags,
            ]}
        />
        <AdvancedFormFields formSchema={[formSchema.active]}/>
    </ActionModalWrapper>
)

// ─── Create ───────────────────────────────────────────────────────────────────

export const CreateMedal = ({
    onCancel,
    owner_id,
    owner_type,
    isSubmitting,
    onSubmit = NullAsDefault,
}) => {
    const t = useTranslate()

    const initialValues = useMemo(
        () => ({
            active: 1,
            owner_id,
            owner_type,
            tags: undefined,
            avatar: undefined,
            title: undefined,
            status: undefined,
            lvl_number: 1,
            description: undefined,
        }),
        [owner_id, owner_type]
    )

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const {owner_id: _oid, owner_type: _otype, avatar_preview, ...rest} = values ?? {}
                const tags = Array.from(values?.tags ?? [])
                onSubmit(
                    _.omitBy(
                        {...rest, tags: tags.join(','), active: Number(values?.active ?? 0)},
                        _.isUndefined
                    )
                )
            }}>
            <MedalFormBody
                isSubmitting={isSubmitting}
                submitLabel="create"
            />
        </Formik>
    )
}

// ─── Edit ─────────────────────────────────────────────────────────────────────

export const EditMedal = ({
    item,
    onCancel,
    owner_id,
    owner_type,
    isSubmitting,
    onSubmit = NullAsDefault,
}) => {
    const t = useTranslate()

    const initialValues = useMemo(() => {
        const {tags, avatar} = item ?? {}
        return {
            ...(item ?? {}),
            owner_id,
            owner_type,
            avatar_preview: avatar,
            tags: Array.from(tags ?? []).map((e) => e?.tags),
        }
    }, [item, owner_id, owner_type])

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, initialValues)
                const {owner_id: _oid, owner_type: _otype, avatar_preview, ...rest} = changes ?? {}
                const tags = Array.from(values?.tags ?? [])
                onSubmit({...rest, tags: tags.join(',')})
            }}>
            <MedalFormBody
                isSubmitting={isSubmitting}
                submitLabel="save"
            />
        </Formik>
    )
}

// ─── Merge ────────────────────────────────────────────────────────────────────

export const MergeMedal = ({
    type = 'student',
    onCancel,
    owner_id,
    owner_type,
    isSubmitting,
    onSubmit = NullAsDefault,
}) => {
    const t = useTranslate()

    const {response} = useAsync({
        apiInfo: medal_getMedals_Api,
        query: bindQueryParams([{prop: owner_type}, {id: owner_id}]),
    })

    const medals = _.get(response, 'data.data', [])

    const validateMerge = useCallback(
        (values) => {
            const {medal_1_id, medal_2_id, new_medal_id} = values ?? {}
            const errors = {}
            if (_.isEmpty(medal_1_id)) errors.medal_1_id = t('required field')
            if (_.isEmpty(medal_2_id)) errors.medal_2_id = t('required field')
            if (_.isEmpty(new_medal_id)) errors.new_medal_id = t('required field')
            return errors
        },
        [t]
    )

    const initialValues = useMemo(
        () => ({
            owner_id,
            owner_type,
            medal_1_id: undefined,
            medal_2_id: undefined,
            new_medal_id: undefined,
            medals,
        }),
        [medals, owner_id, owner_type]
    )

    if (!medals.length) return null

    return (
        <Formik
            validate={validateMerge}
            validateOnMount={true}
            validateOnChange={true}
            initialValues={initialValues}
            onSubmit={(values) => onSubmit(values)}>
            <ActionModalWrapper
                className="gap-4"
                action={
                    <FormActionGroups
                        isSubmitting={isSubmitting}
                        submitProps={{label: 'merge'}}
                    />
                }>
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 flex flex-col gap-1">
                    <span className="text-xs font-semibold text-orange-500 uppercase tracking-wide">
                        {t('notes')}
                    </span>
                    <span className="text-xs text-color-300 leading-relaxed">
                        {t('Students who have obtained Certificate 1 (from a different course) and Certificate 2 (from this course) will be awarded an additional certificate, selected from the options below.')}
                    </span>
                </div>
                <FieldsFactory
                    formSchema={[
                        formSchema.medal_1_id,
                        formSchema.medal_2_id,
                        formSchema.new_medal_id,
                    ]}
                />
            </ActionModalWrapper>
        </Formik>
    )
}
